import type { Todo } from "../../domain/entities.ts";

export function compareTaskOrder(left: Pick<Todo, "pinned" | "position" | "createdAt">, right: Pick<Todo, "pinned" | "position" | "createdAt">): number {
  return Number(!!right.pinned) - Number(!!left.pinned) || (left.position ?? left.createdAt) - (right.position ?? right.createdAt);
}

export function wouldCreateDependencyCycle(taskId: string, dependencyIds: string[], tasks: Pick<Todo, "id" | "dependencyIds">[]): boolean {
  const dependencies = new Map(tasks.map(task => [task.id, task.id === taskId ? dependencyIds : task.dependencyIds || []]));
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string): boolean => {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    for (const dependency of dependencies.get(id) || []) if (visit(dependency)) return true;
    visiting.delete(id);
    visited.add(id);
    return false;
  };
  return visit(taskId);
}

export function incompleteTaskDependencies(task: Pick<Todo, "dependencyIds">, tasks: Todo[]): Todo[] {
  const byId = new Map(tasks.map(item => [item.id, item]));
  return (task.dependencyIds || []).flatMap(id => {
    const dependency = byId.get(id);
    return dependency && !dependency.completed && !dependency.deleted ? [dependency] : [];
  });
}

export function moveItem<T>(items: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return [...items];
  const result = [...items];
  [result[index], result[target]] = [result[target], result[index]];
  return result;
}

export function commandMatches(label: string, query: string): boolean {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const haystack = label.toLowerCase();
  return words.every(word => haystack.includes(word));
}

export function combineConflictValues(field: string, values: unknown[]): unknown {
  if (values.every(value => typeof value === "string")) return [...new Set(values as string[])].filter(Boolean).join(field === "description" ? "\n\n---\n\n" : " / ");
  if (values.every(Array.isArray)) {
    const combined = (values as unknown[][]).flat();
    return [...new Map(combined.map(value => [typeof value === "object" && value && "id" in value ? String((value as { id: unknown }).id) : JSON.stringify(value), value])).values()];
  }
  if (values.every(value => !!value && typeof value === "object" && !Array.isArray(value))) return Object.assign({}, ...values);
  return values.at(-1);
}
